const MAJOR_ARCANA = {
  'the fool': '🃏',
  'the magician': '🪄',
  'the high priestess': '🌙',
  'high priestess': '🌙',
  'the empress': '🌺',
  'the emperor': '👑',
  'the hierophant': '🏛️',
  'the lovers': '💞',
  'the chariot': '🏇',
  'strength': '🦁',
  'lust': '🦁',
  'the hermit': '🕯️',
  'wheel of fortune': '☸️',
  'justice': '⚖️',
  'the hanged man': '🙃',
  'hanged man': '🙃',
  'death': '☠️',
  'temperance': '🌈',
  'art': '🌈',
  'the devil': '⛓️',
  'the tower': '⚡',
  'the star': '⭐',
  'the moon': '🌓',
  'the sun': '☀️',
  'judgement': '📯',
  'judgment': '📯',
  'aeon': '📯',
  'the world': '🌍',
  'the universe': '🌍',
  // short aliases (checked after full names)
  'fool': '🃏',
  'magician': '🪄',
  'empress': '🌺',
  'emperor': '👑',
  'hierophant': '🏛️',
  'lovers': '💞',
  'chariot': '🏇',
  'hermit': '🕯️',
  'wheel': '☸️',
  'tower': '⚡',
  'star': '⭐',
  'moon': '🌕',
  'sun': '☀️',
  'world': '🌍',
  'universe': '🌍',
  'devil': '⛓️',
};

const SUIT_COLORS = {
  wands: '#E07820',
  fire: '#E07820',
  bows: '#E07820',
  cups: '#4A8FD4',
  water: '#4A8FD4',
  vessels: '#4A8FD4',
  swords: '#C4A800',
  air: '#C4A800',
  arrows: '#C4A800',
  pentacles: '#8B5E1A',
  earth: '#8B5E1A',
  stones: '#8B5E1A',
  coins: '#8B5E1A',
  disks: '#8B5E1A',
  discs: '#8B5E1A',
};

export const ELEMENT_COLORS = {
  fire: '#E07820',
  water: '#4A8FD4',
  air: '#C4A800',
  earth: '#8B5E1A',
  major: '#9B59B6',
};

export const ELEMENT_LABELS = {
  fire: 'Fire 🔥',
  water: 'Water 💧',
  air: 'Air 💨',
  earth: 'Earth 🌿',
  major: 'Major ✨',
};

const SUIT_TO_ELEMENT = {
  wands: 'fire', fire: 'fire', bows: 'fire',
  cups: 'water', water: 'water', vessels: 'water',
  swords: 'air', air: 'air', arrows: 'air',
  pentacles: 'earth', earth: 'earth', stones: 'earth', coins: 'earth', disks: 'earth', discs: 'earth'
};

const NUMBER_WORDS = {
  one: 'ace', two: '2', three: '3', four: '4', five: '5',
  six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
};

const COURT_SYMBOLS = {
  page: '🜃', princess: '🜃',
  knight: '🜂',
  prince: '🜁', king: '🜁',
  queen: '🜄',
};

export function parseCard(text) {
  if (!text) return null;
  // Split into lines — first matching line wins
  const lines = text.split(/[\n,;]/).map(l => l.trim()).filter(Boolean);
  for (const line of lines) {
    const result = _parseLine(line);
    if (result) return result;
  }
  return null;
}

function _parseLine(text) {
  const isReversed = /reversed|\(r\)/i.test(text);
  const lower = text.toLowerCase()
    .replace(/\s*\(r\)\s*/gi, '')
    .replace(/\s*reversed\s*/gi, '')
    .trim();

  // Major arcana — longest key first to avoid partial matches
  const majorKeys = Object.keys(MAJOR_ARCANA).sort((a, b) => b.length - a.length);
  for (const key of majorKeys) {
    if (lower === key || lower.includes(key)) {
      return { type: 'major', name: text.trim(), emoji: MAJOR_ARCANA[key], reversed: isReversed };
    }
  }

  // Minor arcana — find "X of Y" anywhere in the line (e.g. "7 of wands", "Knight of Swords")
  const match = lower.match(/\b(\w+)\s+of\s+(\w+)/);
  if (match) {
    let rankStr = match[1].trim();
    const suitStr = match[2].trim();
    if (NUMBER_WORDS[rankStr]) rankStr = NUMBER_WORDS[rankStr];
    if (rankStr === '1') rankStr = 'ace';

    const suitKey = Object.keys(SUIT_COLORS).find(k => suitStr.includes(k));
    const color = suitKey ? SUIT_COLORS[suitKey] : null;
    const element = suitKey ? SUIT_TO_ELEMENT[suitKey] : null;

    let symbol = null;
    if (rankStr === 'ace') symbol = 'A';
    else if (COURT_SYMBOLS[rankStr]) symbol = COURT_SYMBOLS[rankStr];
    else if (!isNaN(parseInt(rankStr))) symbol = String(parseInt(rankStr));

    if (color && symbol) {
      return { type: 'minor', name: text.trim(), suit: suitKey, element, color, symbol, rank: rankStr, reversed: isReversed };
    }
  }

  return null;
}

// Pull the card name from a calendar day-map entry
export function getCardName(entry) {
  return entry?.momentText || entry?.value?.title || entry?.value?.text || null;
}

export function getElement(card) {
  if (!card) return null;
  return card.type === 'major' ? 'major' : card.element;
}

export function computeStats(cards) {
  const total = cards.length;
  if (!total) return null;

  const elements = { fire: 0, water: 0, air: 0, earth: 0, major: 0 };
  const numberCounts = {};
  const suitCounts = {};
  const nameCounts = {};
  let courtCount = 0;
  let reversedCount = 0;

  for (const card of cards) {
    const el = getElement(card);
    if (el && elements[el] !== undefined) elements[el]++;
    if (card.reversed) reversedCount++;

    const key = card.name?.toLowerCase().trim();
    if (key) nameCounts[key] = (nameCounts[key] || 0) + 1;

    if (card.type === 'minor') {
      if (card.suit) suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
      if (card.rank) numberCounts[card.rank] = (numberCounts[card.rank] || 0) + 1;
      if (['page', 'princess', 'knight', 'prince', 'king', 'queen'].includes(card.rank)) courtCount++;
    }
  }

  const suitsByFrequency = Object.entries(suitCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([suit, count]) => ({ suit, count }));

  const repeatedCards = Object.entries(nameCounts)
    .filter(([, count]) => count > 1)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));

  const repeatedRanks = Object.entries(numberCounts)
    .filter(([, count]) => count > 1)
    .sort((a, b) => b[1] - a[1])
    .map(([rank, count]) => ({ rank, count }));

  const topElement = Object.entries(elements).sort((a, b) => b[1] - a[1])[0];

  return {
    total,
    elements,
    suitsByFrequency,
    repeatedCards,
    repeatedRanks,
    topElement: topElement ? { element: topElement[0], count: topElement[1] } : null,
    courtCount,
    reversedCount,
    majorPercent: total ? Math.round((elements.major / total) * 100) : 0,
  };
}

export default { parseCard, getCardName, getElement, computeStats, ELEMENT_COLORS, ELEMENT_LABELS, SUIT_COLORS };
